<template>
  <div class="booking-area">
    <div class="container">
      <div class="ticket-search-panel">
        <div class="ticket-search-title">
          <i class="ph ph-train"></i>
          <span>Поиск билетов</span>
        </div>
        <form class="ticket-search-form" @submit.prevent="handleSubmit">
          <div ref="fromDropdown" class="ticket-field ticket-field--station">
            <label class="ticket-field__label">Откуда</label>
            <button class="ticket-pill ticket-pill--station" type="button" @click="toggleStationDropdown('from')">
              <i class="ph ph-map-pin ticket-pill__icon"></i>
              <span class="ticket-station">
                <strong>{{ stationTitle(fromCity) || 'Откуда' }}</strong>
                <small v-if="stationSubtitle(fromCity)">{{ stationSubtitle(fromCity) }}</small>
              </span>
              <i class="ph ph-caret-down ticket-pill__chevron"></i>
            </button>
            <div v-if="activeStationDropdown === 'from'" class="ticket-dropdown">
              <div class="ticket-dropdown__header">
                <strong>Станция отправления</strong>
                <button type="button" @click="activeStationDropdown = null">Закрыть</button>
              </div>
              <input
                v-model="fromSearch"
                class="ticket-dropdown__search"
                type="text"
                placeholder="Поиск станции"
                autocomplete="off"
              />
              <div class="ticket-dropdown__list">
                <button
                  v-for="station in filteredFromStations"
                  :key="station.id"
                  class="ticket-dropdown__option"
                  type="button"
                  @click="selectStation('from', station)"
                >
                  <strong>{{ stationTitle(station) }}</strong>
                  <small v-if="stationSubtitle(station)">{{ stationSubtitle(station) }}</small>
                </button>
                <div v-if="!filteredFromStations.length" class="ticket-dropdown__empty">Ничего не найдено</div>
              </div>
            </div>
          </div>

          <button class="ticket-swap" type="button" aria-label="Поменять станции местами" @click="swap">
            <i class="ph ph-repeat"></i>
          </button>

          <div ref="toDropdown" class="ticket-field ticket-field--station">
            <label class="ticket-field__label">Куда</label>
            <button class="ticket-pill ticket-pill--station" type="button" @click="toggleStationDropdown('to')">
              <i class="ph ph-flag ticket-pill__icon"></i>
              <span class="ticket-station">
                <strong>{{ stationTitle(toCity) || 'Куда' }}</strong>
                <small v-if="stationSubtitle(toCity)">{{ stationSubtitle(toCity) }}</small>
              </span>
              <i class="ph ph-caret-down ticket-pill__chevron"></i>
            </button>
            <div v-if="activeStationDropdown === 'to'" class="ticket-dropdown">
              <div class="ticket-dropdown__header">
                <strong>Станция прибытия</strong>
                <button type="button" @click="activeStationDropdown = null">Закрыть</button>
              </div>
              <input
                v-model="toSearch"
                class="ticket-dropdown__search"
                type="text"
                placeholder="Поиск станции"
                autocomplete="off"
              />
              <div class="ticket-dropdown__list">
                <button
                  v-for="station in filteredToStations"
                  :key="station.id"
                  class="ticket-dropdown__option"
                  type="button"
                  @click="selectStation('to', station)"
                >
                  <strong>{{ stationTitle(station) }}</strong>
                  <small v-if="stationSubtitle(station)">{{ stationSubtitle(station) }}</small>
                </button>
                <div v-if="!filteredToStations.length" class="ticket-dropdown__empty">Ничего не найдено</div>
              </div>
            </div>
          </div>

          <div class="ticket-field ticket-field--type">
            <label class="ticket-field__label">Тип поездки</label>
            <div class="ticket-trip-toggle">
              <button
                type="button"
                :class="{ active: tripType === 'one_way' }"
                @click="setTripType('one_way')"
              >
                В одну сторону
              </button>
              <button
                type="button"
                :class="{ active: tripType === 'round_trip' }"
                @click="setTripType('round_trip')"
              >
                Туда-обратно
              </button>
            </div>
          </div>

          <div ref="dateDropdown" class="ticket-field ticket-field--date">
            <label class="ticket-field__label">Дата</label>
            <button class="ticket-pill ticket-pill--date" type="button" @click="toggleDatePicker('departure')">
              <i class="ph ph-calendar-blank ticket-pill__icon"></i>
              <span>{{ dateDisplay }}</span>
            </button>
            <div v-if="activeDatePicker === 'departure'" class="ticket-calendar">
              <div class="ticket-calendar__header">
                <strong>{{ tripType === 'round_trip' ? 'Выберите даты туда и обратно' : 'Выберите дату' }}</strong>
                <button type="button" @click="activeDatePicker = null">Закрыть</button>
              </div>
              <DatePicker
                v-model="calendarModel"
                :is-range="tripType === 'round_trip'"
                :min-date="new Date()"
                :columns="columns"
                :rows="rows"
                borderless
                @update:modelValue="onDateSelected"
              />
            </div>
          </div>

          <button class="ticket-submit" type="submit" :disabled="isLoading">
            <i :class="isLoading ? 'ph ph-spinner-gap' : 'ph ph-magnifying-glass'"></i>
            <span>Найти</span>
          </button>
        </form>
        <p v-if="searchError" class="ticket-search-error">{{ searchError }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import moment from 'moment'
import axios from 'axios'
import { DatePicker } from 'v-calendar'
import { onClickOutside } from '@vueuse/core'
import { useScreens } from 'vue-screen-utils'
import i18n from '@/local18'

import '@/assets/css/home/booking.css'
import 'v-calendar/style.css'

const TICKET_FRONTEND_URL = process.env.VUE_APP_TICKET_FRONTEND_URL || 'https://ticket.railway.kg'
const TICKET_API_BASE_URL = process.env.VUE_APP_TICKET_API_BASE_URL || 'https://ticket.railway.kg/api/v1'
const PRIORITY_STATIONS = [
  'Бишкек 2',
  'Бишкек 1',
  'Балыкчы',
  'Балыкчы пляж',
  'Токмок',
  'Каинды',
  'Кара-Балта',
  'Кант',
  'Сокулук',
  'Ивановка',
  'Новопокровка',
].map(normalizeSearch)

const { mapCurrent } = useScreens({ xs: '0px', sm: '640px', md: '768px', lg: '1024px', xl: '1240px' })
const columns = mapCurrent({ xl: 2 }, 1)
const rows = mapCurrent({ xl: 1 }, 1)

const route = useRoute()

const stations = ref([])
const fromCity = ref(null)
const toCity = ref(null)
const fromSearch = ref('')
const toSearch = ref('')
const activeStationDropdown = ref(null)
const activeDatePicker = ref(null)
const departureDate = ref(new Date())
const returnDate = ref(null)
const tripType = ref('one_way')
const isLoading = ref(false)
const searchError = ref('')

const fromDropdown = ref(null)
const toDropdown = ref(null)
const dateDropdown = ref(null)

onClickOutside(fromDropdown, () => closeStationDropdown('from'))
onClickOutside(toDropdown, () => closeStationDropdown('to'))
onClickOutside(dateDropdown, () => closeDatePicker('departure'))

const filteredFromStations = computed(() => filterStations(fromSearch.value))
const filteredToStations = computed(() => filterStations(toSearch.value))
const dateDisplay = computed(() => {
  if (tripType.value === 'round_trip') {
    return returnDate.value
      ? `${formatDate(departureDate.value)} - ${formatDate(returnDate.value)}`
      : `${formatDate(departureDate.value)} - обратно`
  }

  return formatDate(departureDate.value)
})
const calendarModel = computed({
  get() {
    if (tripType.value === 'round_trip') {
      return {
        start: departureDate.value,
        end: returnDate.value || departureDate.value,
      }
    }

    return departureDate.value
  },
  set(value) {
    if (tripType.value === 'round_trip') {
      if (value?.start) departureDate.value = value.start
      if (value?.end) returnDate.value = value.end
      return
    }

    if (value) departureDate.value = value
  },
})

async function fetchStations() {
  try {
    const response = await axios.get(`${TICKET_API_BASE_URL}/sales/stations/`)
    const stationList = Array.isArray(response.data) ? response.data : response.data.results || []
    stations.value = stationList.map(normalizeStation).sort(compareStations)
  } catch (error) {
    searchError.value = 'Не удалось загрузить станции.'
    console.error('API data fetch error:', error)
  }
}

function normalizeStation(station) {
  return {
    ...station,
    name_ru: station.name_ru || station.title_ru || '',
    name_en: station.name_en || station.title_en || station.title_ru || '',
    name_kg: station.name_kg || station.title_ky || station.title_ru || '',
    city_name_ru: station.city_name_ru || station.city_title_ru || '',
    city_name_en: station.city_name_en || station.city_title_en || station.city_title_ru || '',
    city_name_kg: station.city_name_kg || station.city_title_ky || station.city_title_ru || '',
  }
}

function filterStations(query) {
  const search = normalizeSearch(query)
  if (!search) return stations.value

  return stations.value.filter((station) => {
    return stationSearchText(station).includes(search)
  })
}

function compareStations(firstStation, secondStation) {
  const firstPriority = priorityStationIndex(firstStation)
  const secondPriority = priorityStationIndex(secondStation)

  if (firstPriority !== secondPriority) return firstPriority - secondPriority

  return stationTitle(firstStation).localeCompare(stationTitle(secondStation), 'ru')
}

function priorityStationIndex(station) {
  const title = normalizeSearch(station.name_ru || station.name_kg || station.name_en)
  const text = stationSearchText(station)
  const exactIndex = PRIORITY_STATIONS.findIndex((priorityStation) => title === priorityStation)
  if (exactIndex !== -1) return exactIndex

  const partialMatch = PRIORITY_STATIONS
    .map((priorityStation, index) => ({ index, priorityStation }))
    .filter(({ priorityStation }) => title.includes(priorityStation) || text.includes(priorityStation))
    .sort((first, second) => second.priorityStation.length - first.priorityStation.length)[0]

  return partialMatch ? partialMatch.index : Number.MAX_SAFE_INTEGER
}

function stationSearchText(station) {
  return normalizeSearch([
    station.name_ru,
    station.name_en,
    station.name_kg,
    station.city_name_ru,
    station.city_name_en,
    station.city_name_kg,
  ].filter(Boolean).join(' '))
}

function normalizeSearch(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^a-zа-я0-9]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function stationTitle(station) {
  if (!station) return ''
  const locale = i18n.global.locale.value
  if (locale === 'en') return station.name_en || station.name_ru || station.name_kg
  if (locale === 'kg') return station.name_kg || station.name_ru || station.name_en
  return station.name_ru || station.name_kg || station.name_en
}

function stationSubtitle(station) {
  if (!station) return ''
  const locale = i18n.global.locale.value
  if (locale === 'en') return station.city_name_en || station.city_name_ru || ''
  if (locale === 'kg') return station.city_name_kg || station.city_name_ru || ''
  return station.city_name_ru || ''
}

function toggleStationDropdown(type) {
  activeStationDropdown.value = activeStationDropdown.value === type ? null : type
  activeDatePicker.value = null
}

function closeStationDropdown(type) {
  if (activeStationDropdown.value === type) {
    activeStationDropdown.value = null
  }
}

function selectStation(type, station) {
  if (type === 'from') {
    fromCity.value = station
    fromSearch.value = ''
  } else {
    toCity.value = station
    toSearch.value = ''
  }
  activeStationDropdown.value = null
}

function swap() {
  const from = fromCity.value
  fromCity.value = toCity.value
  toCity.value = from
}

function setTripType(type) {
  tripType.value = type
  if (type === 'one_way') {
    returnDate.value = null
  }
}

function toggleDatePicker() {
  activeDatePicker.value = activeDatePicker.value === 'departure' ? null : 'departure'
  activeStationDropdown.value = null
}

function closeDatePicker(type) {
  if (activeDatePicker.value === type) {
    activeDatePicker.value = null
  }
}

function onDateSelected(value) {
  if (tripType.value === 'round_trip') {
    if (value?.start) departureDate.value = value.start
    if (value?.end) returnDate.value = value.end
    if (value?.start && value?.end) activeDatePicker.value = null
    return
  }

  activeDatePicker.value = null
}

function formatDate(date) {
  return moment(date).format('DD.MM.YYYY')
}

function normalizeRouteDate(value) {
  const raw = Array.isArray(value) ? value[0] : value
  if (!raw) return null
  const parsed = moment(raw, 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed.toDate() : null
}

function applyRouteDates() {
  const startDate = normalizeRouteDate(route.query.departure_date || route.query.start_date)
  const endDate = normalizeRouteDate(route.query.return_date || route.query.end_date)

  if (startDate) departureDate.value = startDate
  if (endDate) {
    returnDate.value = endDate
    tripType.value = 'round_trip'
  }
}

function handleSubmit() {
  searchError.value = ''
  activeStationDropdown.value = null
  activeDatePicker.value = null

  if (!fromCity.value?.id || !toCity.value?.id) {
    searchError.value = 'Выберите станции отправления и прибытия.'
    return
  }

  if (fromCity.value.id === toCity.value.id) {
    searchError.value = 'Станции отправления и прибытия должны отличаться.'
    return
  }

  if (tripType.value === 'round_trip' && !returnDate.value) {
    searchError.value = 'Выберите дату обратно.'
    return
  }

  const searchUrl = new URL('/', TICKET_FRONTEND_URL)
  const startDate = moment(departureDate.value).format('YYYY-MM-DD')
  const endDate = returnDate.value ? moment(returnDate.value).format('YYYY-MM-DD') : ''

  searchUrl.searchParams.set('from_station', fromCity.value.id)
  searchUrl.searchParams.set('to_station', toCity.value.id)
  searchUrl.searchParams.set('departure_date', startDate)
  searchUrl.searchParams.set('trip_type', tripType.value)

  if (tripType.value === 'round_trip' && endDate) {
    searchUrl.searchParams.set('return_date', endDate)
  }

  window.location.href = searchUrl.toString()
}

onMounted(() => {
  applyRouteDates()
  fetchStations()
})
</script>

<style scoped>
.ticket-search-panel {
  position: relative;
  z-index: 5;
  width: 100%;
  padding: 20px 24px;
  border: 1px solid rgba(197, 219, 242, 0.72);
  border-radius: 18px;
  background: rgba(239, 247, 252, 0.86);
  box-shadow: 0 18px 42px rgba(24, 56, 96, 0.12);
  backdrop-filter: blur(16px);
}

.ticket-search-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 4px;
  color: #0b73dc;
  font-size: 14px;
  font-weight: 850;
  letter-spacing: 0.2px;
  line-height: 1;
  text-transform: uppercase;
}

.ticket-search-title i {
  font-size: 18px;
}

.ticket-search-form {
  display: grid;
  grid-template-columns: minmax(180px, 1.35fr) 52px minmax(180px, 1.35fr) minmax(178px, 1fr) minmax(170px, 0.9fr) minmax(142px, 0.82fr);
  gap: 12px;
  align-items: end;
}

.ticket-field {
  position: relative;
  min-width: 0;
}

.ticket-field__label {
  display: block;
  margin: 0 0 8px 4px;
  color: #173a63;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.1;
}

.ticket-pill,
.ticket-submit,
.ticket-swap,
.ticket-trip-toggle {
  min-height: 58px;
  border: 0;
  border-radius: 16px;
}

.ticket-pill {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 11px 15px;
  border: 1px solid #d9e8f7;
  background: #fff;
  color: #173a63;
  text-align: left;
  box-shadow: 0 8px 18px rgba(26, 82, 138, 0.08);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.ticket-pill:hover,
.ticket-pill:focus-visible {
  border-color: #8dbde9;
  box-shadow: 0 12px 26px rgba(35, 107, 177, 0.14);
  outline: none;
}

.ticket-pill--date {
  grid-template-columns: auto minmax(0, 1fr);
  font-size: 18px;
  font-weight: 750;
}

.ticket-pill--date span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-pill__icon {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  background: #eaf4ff;
  color: #1468c9;
  font-size: 20px;
}

.ticket-pill__chevron {
  color: #5d7693;
  font-size: 17px;
}

.ticket-station {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.05;
}

.ticket-station strong {
  overflow: hidden;
  color: #183755;
  font-size: 19px;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-station small {
  overflow: hidden;
  margin-top: 5px;
  color: #6b7f94;
  font-size: 12px;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-swap {
  display: grid;
  place-items: center;
  width: 52px;
  height: 58px;
  background: #0b73dc;
  color: #fff;
  font-size: 24px;
  box-shadow: 0 12px 24px rgba(11, 115, 220, 0.28);
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.ticket-swap:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px rgba(11, 115, 220, 0.34);
}

.ticket-trip-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;
  padding: 4px;
  border: 1px solid #d9e8f7;
  background: #f6fbff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.8);
}

.ticket-trip-toggle button {
  min-width: 0;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #526a84;
  font-size: 13px;
  font-weight: 850;
  line-height: 1.05;
  transition: background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;
}

.ticket-trip-toggle button.active {
  background: #0b73dc;
  color: #fff;
  box-shadow: 0 8px 18px rgba(11, 115, 220, 0.22);
}

.ticket-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 0 22px;
  background: #0b73dc;
  color: #fff;
  font-size: 21px;
  font-weight: 800;
  box-shadow: 0 14px 30px rgba(11, 115, 220, 0.3);
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.ticket-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 18px 38px rgba(11, 115, 220, 0.36);
}

.ticket-submit:disabled {
  cursor: wait;
  opacity: 0.75;
}

.ticket-dropdown,
.ticket-calendar {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: 80;
  width: min(380px, 92vw);
  overflow: hidden;
  border: 1px solid #d9e8f7;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 22px 52px rgba(17, 52, 88, 0.22);
}

.ticket-dropdown {
  display: flex;
  max-height: 390px;
  flex-direction: column;
}

.ticket-calendar {
  width: auto;
  padding: 12px;
}

.ticket-dropdown__header,
.ticket-calendar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 14px 0;
  color: #173a63;
}

.ticket-calendar__header {
  padding: 2px 4px 10px;
}

.ticket-dropdown__header button,
.ticket-calendar__header button {
  border: 0;
  background: transparent;
  color: #0b73dc;
  font-size: 13px;
  font-weight: 800;
}

.ticket-dropdown__search {
  width: calc(100% - 28px);
  margin: 12px 14px;
  padding: 11px 14px;
  border: 1px solid #d9e8f7;
  border-radius: 12px;
  outline: none;
  color: #173a63;
}

.ticket-dropdown__search:focus {
  border-color: #8dbde9;
  box-shadow: 0 0 0 3px rgba(11, 115, 220, 0.1);
}

.ticket-dropdown__list {
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 8px;
}

.ticket-dropdown__option {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
  border: 0;
  border-top: 1px solid #edf4fb;
  background: #fff;
  padding: 12px 16px;
  color: #173a63;
  text-align: left;
}

.ticket-dropdown__option:hover {
  background: #f2f8fe;
}

.ticket-dropdown__option strong {
  font-size: 15px;
}

.ticket-dropdown__option small {
  color: #6b7f94;
}

.ticket-dropdown__empty {
  padding: 18px;
  color: #6b7f94;
  text-align: center;
}

.ticket-search-error {
  margin: 14px 4px 0;
  color: #b42318;
  font-size: 14px;
  font-weight: 800;
}

@media (max-width: 1199px) {
  .ticket-search-form {
    grid-template-columns: minmax(180px, 1fr) 52px minmax(180px, 1fr);
  }

  .ticket-field--type,
  .ticket-field--date,
  .ticket-submit {
    grid-column: span 1;
  }
}

@media (max-width: 991px) {
  .ticket-search-form {
    grid-template-columns: minmax(0, 1fr) 52px minmax(0, 1fr);
  }

  .ticket-field--type,
  .ticket-field--date,
  .ticket-submit {
    grid-column: 1 / -1;
  }

  .ticket-submit {
    min-height: 56px;
  }
}

@media (max-width: 767px) {
  .ticket-search-panel {
    padding: 16px;
    border-radius: 18px;
  }

  .ticket-search-title {
    display: flex;
    justify-content: center;
    margin: 0 0 18px;
    font-size: 13px;
  }

  .ticket-search-form {
    position: relative;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .ticket-swap {
    position: absolute;
    top: 78px;
    right: 13px;
    z-index: 3;
    width: 48px;
    height: 48px;
    min-height: 48px;
    border-radius: 14px;
  }

  .ticket-pill,
  .ticket-trip-toggle,
  .ticket-submit {
    min-height: 56px;
    border-radius: 15px;
  }

  .ticket-station strong {
    font-size: 18px;
  }

  .ticket-dropdown {
    position: fixed;
    top: max(12px, env(safe-area-inset-top));
    right: 12px;
    bottom: max(12px, env(safe-area-inset-bottom));
    left: 12px;
    width: auto;
    max-height: none;
    border-radius: 18px;
  }

  .ticket-dropdown__header {
    padding: 16px 16px 4px;
  }

  .ticket-dropdown__search {
    width: calc(100% - 32px);
    margin: 12px 16px;
  }

  .ticket-calendar {
    right: 0;
    left: 0;
    width: auto;
  }
}
</style>
